import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from './shared/useAuthModal';
import { loginSchema } from './shared/validationSchemas';
import ModalWrapper from './shared/ModalWrapper';
import SocialLoginButton from './shared/SocialLoginButton';
import FormInput from './shared/FormInput';
import ErrorDisplay from './shared/ErrorDisplay';
import CloseButton from './shared/CloseButton';
import './AuthModal.css';

const LoginModal = ({ isOpen, onClose, onSwitchToSignup }) => {
  const { loginWithEmail, loginWithGoogleOAuth } = useAuth();
  const { isLoading, setIsLoading, error, clearError, handleSocialAuth } = useAuthModal();
  const [localError, setLocalError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(loginSchema)
  });

  const handleClose = () => {
    setIsLoading(false);
    clearError();
    setLocalError('');
    reset();
    onClose();
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    setLocalError('');
    clearError();
    try {
      await loginWithEmail(data.email, data.password);
      handleClose();
    } catch (err) {
      setLocalError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    handleSocialAuth(loginWithGoogleOAuth, handleClose);
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={handleClose} className="auth-modal-login">
      <div className="auth-modal-content auth-modal-content-login">
        <div className="auth-modal-panel">
          <div className="auth-modal-header auth-modal-header-login">
            <div className="auth-modal-header-content">
              <h2 className="auth-modal-title-login">Welcome back</h2>
              <p className="auth-modal-subtitle-login">
                Login to your organiser profile
              </p>
            </div>
            <CloseButton
              className="auth-modal-close-login"
              onClick={handleClose}
            />
          </div>

          <div className="auth-modal-body auth-modal-body-login">
            <form onSubmit={handleSubmit(onSubmit)} className="auth-form auth-form-login">
              <FormInput
                type="email"
                placeholder="Email"
                register={register}
                error={errors.email}
                name="email"
              />

              <FormInput
                type="password"
                placeholder="Password"
                register={register}
                error={errors.password}
                name="password"
              />

              <ErrorDisplay error={localError || error} />

              <button
                type="submit"
                className="auth-submit-btn auth-submit-btn-login"
                disabled={isLoading}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>

            <div className="auth-divider auth-divider-login">
              <div className="auth-divider-line"></div>
              <span className="auth-divider-text auth-divider-text-login">or</span>
              <div className="auth-divider-line"></div>
            </div>

            <div className="auth-social-buttons auth-social-buttons-login">
              <SocialLoginButton
                provider="google"
                onClick={handleGoogleLogin}
                disabled={isLoading}
                loadingText="Logging in..."
              >
                Login with Google
              </SocialLoginButton>
            </div>

            <div className="auth-modal-footer auth-modal-footer-login">
              <p className="auth-switch-text-login">
                New to Occasia?{' '}
                <button
                  type="button"
                  className="auth-switch-link-login"
                  onClick={onSwitchToSignup}
                >
                  Join for free
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default LoginModal;
